<script setup>
import { onMounted, ref, computed } from "vue";
import Menu from 'primevue/menu';
import { useAuthStore } from "@/stores/authStore";
import { isOnline } from "@/services/healthCheck";
import ManualOrderPopup from "../manual-order/LoadManualOrderPopup.vue";
import StoreCreditPopup from "../Return/StoreCreditPopup.vue";
import CheckInventoryPopup from "../Inventory/CheckInventoryPopup.vue";
import PrintLabelPopup from "../EmbeddedCode/PrintLabelPopup.vue";
import BarcodeGeneratorPopup from "../BarcodeGenerator/BarcodeGeneratorPopup.vue";
import ErrorLogViewerPopup from "../Popups/ErrorLogViewerPopup.vue";
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

const authStore = useAuthStore();

const menu = ref();
const items = computed(() => ([
    {
        items: [
            {
                label: 'Refund',
                icon: 'pi pi-refresh',
                class: 'menu-item',
                requiresOnline: true,           // returns hit the server — offline-disabled
                disabled: !isOnline.value,
                command: () => {
                    authStore.toggleRefundPopup();
                }
            },
            {
                label: 'Manual Order',
                icon: 'pi pi-download',
                class: 'menu-item',
                requiresOnline: true,           // loads manual orders from the server
                disabled: !isOnline.value,
                command: () => {
                    authStore.toggleManualOrderPopup();
                }
            },
            ...(authStore.canDoEmbedded
                ? [{
                    label: 'Embedded Barcode',
                    icon: 'pi pi-print',
                    class: 'menu-item',
                    requiresOnline: true,       // /api/Barcode/* — server-side
                    disabled: !isOnline.value,
                    command: () => authStore.togglePrintLabelPopup()
                }]
                : []),
            ...(authStore.canDoEmbedded
                ? [{
                    label: 'Barcode Generator',
                    icon: 'pi pi-barcode',
                    class: 'menu-item',
                    command: () => authStore.toggleBarcodeGenerator()
                }]
                : []),
            ...(authStore.isManagerLoggedIn
                ? [{
                    label: 'Error Log',
                    icon: 'pi pi-exclamation-circle',
                    class: 'menu-item',
                    requiresOnline: true,       // reads server-stored logs
                    disabled: !isOnline.value,
                    command: () => authStore.toggleErrorLogViewer()
                }]
                : []),
        ]
    }
]));

const toggle = (event) => {
    menu.value.toggle(event);
};
onMounted(() => {
    tippy('.menu-container', {
        content: 'Actions',
        animation: 'scale',
        // placement: 'bottom',
    });
});
</script>

<template>
    <div class="menu-container">
        <Button
            :disabled="!(authStore.isUserLoggedIn || authStore.isManagerLoggedIn)"
            :class="{ 'opacity-50 cursor-not-allowed': !(authStore.isUserLoggedIn || authStore.isManagerLoggedIn) }"
            class="menu-button"
            type="button" 
            @click="toggle" 
            aria-haspopup="true" 
            aria-controls="overlay_menu"
            text
            rounded
        >
            <img src="/menu.svg" alt="Menu" class="menu-icon">
        </Button>
        <Menu
            ref="menu"
            id="overlay_menu"
            :model="items"
            :popup="true"
        >
            <template #item="{ item, props }">
                <a class="p-menuitem-link req-online-link" :class="{ 'req-online-off': item.requiresOnline && !isOnline }" v-bind="props.action">
                    <span :class="item.icon" />
                    <span class="p-menuitem-text">{{ item.label }}</span>
                    <span
                        v-if="item.requiresOnline && !isOnline"
                        class="req-online-dot"
                        title="Requires an internet connection"
                    />
                </a>
            </template>
        </Menu>
    </div>
    <ManualOrderPopup v-if="authStore.isManualOrderPopupVisible && (authStore.isUserLoggedIn || authStore.isManagerLoggedIn)" />
    <StoreCreditPopup v-if="authStore.isRefundPopupVisible && (authStore.isUserLoggedIn || authStore.isManagerLoggedIn)" />
    <PrintLabelPopup v-if="authStore.printLabelPopupVisible && (authStore.isUserLoggedIn || authStore.isManagerLoggedIn)" />
    <BarcodeGeneratorPopup v-if="authStore.barcodeGeneratorVisible && (authStore.isUserLoggedIn || authStore.isManagerLoggedIn)" />
    <ErrorLogViewerPopup v-if="authStore.isErrorLogViewerVisible && authStore.isManagerLoggedIn" />
</template>

<style scoped>
.menu-container {
    position: relative;
}

.menu-button {
    padding: 0.5rem;
    transition: background-color 0.2s;
}

.menu-button:hover {
    background-color: var(--primary-color-light);
}

.menu-icon {
    width: 1.5rem;
    height: 1.5rem;
}
</style>

<style>
.p-menu {
    --p-menu-background: #ffffff;
    --p-menu-border-radius: 0.5rem;
    --p-menu-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --p-menu-list-padding: 0.5rem;
    --p-menu-list-gap: 0.25rem;
    --p-menu-item-padding: 0.75rem 1rem;
    --p-menu-item-border-radius: 0.375rem;
    --p-menu-item-gap: 0.75rem;
    --p-menu-item-icon-color: var(--primary-color);
    --p-menu-separator-border-color: #e5e7eb;
    width: fit-content;
    min-width: max-content;
}

.p-menu .menu-header {
    font-weight: 600;
    color: var(--text-color-secondary);
    padding: 0.75rem 1rem;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
}

.p-menu .menu-item .p-menuitem-link {
    transition: all 0.2s ease;
}

.p-menu .menu-item .p-menuitem-text {
    white-space: nowrap;
}

.p-menu .menu-item .p-menuitem-link:hover {
    background-color: var(--p-menu-item-focus-background, #f3f4f6);
    color: var(--p-menu-item-focus-color, inherit);
}

.p-menu .menu-item .p-menuitem-link:hover .pi {
    color: var(--p-menu-item-icon-focus-color, var(--primary-color));
}

/* Online-only items: greyed + non-interactive + a pulsing red dot (matches the offline pill). */
.req-online-link { display: flex; align-items: center; gap: 0.75rem; }
.req-online-off { opacity: 0.45; cursor: not-allowed; pointer-events: none; }
.req-online-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #dc2626;
    margin-left: auto; flex-shrink: 0; animation: req-pulse 1.4s infinite;
}
@keyframes req-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
</style>